import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const emirateRates = {
  Dubai: { baseRate: 4.49, minDP_expat: 25, minDP_national: 20, transferFee: 4, adminFee: 580, label: 'Dubai' },
  'Abu Dhabi': { baseRate: 4.35, minDP_expat: 25, minDP_national: 20, transferFee: 2, adminFee: 1000, label: 'Abu Dhabi' },
  Sharjah: { baseRate: 4.75, minDP_expat: 30, minDP_national: 25, transferFee: 4, adminFee: 500, label: 'Sharjah' },
  Ajman: { baseRate: 4.85, minDP_expat: 30, minDP_national: 25, transferFee: 4, adminFee: 500, label: 'Ajman' },
  'Ras Al Khaimah': { baseRate: 4.65, minDP_expat: 25, minDP_national: 20, transferFee: 4, adminFee: 500, label: 'Ras Al Khaimah' },
  Fujairah: { baseRate: 4.90, minDP_expat: 30, minDP_national: 25, transferFee: 4, adminFee: 500, label: 'Fujairah' },
  'Umm Al Quwain': { baseRate: 4.95, minDP_expat: 30, minDP_national: 25, transferFee: 4, adminFee: 500, label: 'Umm Al Quwain' },
};

const UAEMortgageFinance = () => {
  const navigate = useNavigate();
  const [selectedEmirate, setSelectedEmirate] = useState('Dubai');
  const [propertyPrice, setPropertyPrice] = useState('1500000');
  const [downPayment, setDownPayment] = useState('25');
  const [interestRate, setInterestRate] = useState('4.49');
  const [tenure, setTenure] = useState('25');
  const [buyerType, setBuyerType] = useState('expat');
  const [activeTab, setActiveTab] = useState('calculator');
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const calcTimerRef = React.useRef(null);

  const triggerCalc = () => {
    setIsCalculating(true);
    if (calcTimerRef?.current) clearTimeout(calcTimerRef?.current);
    calcTimerRef.current = setTimeout(() => setIsCalculating(false), 500);
  };

  const emirateData = emirateRates?.[selectedEmirate];
  const minDP = buyerType === 'expat' ? emirateData?.minDP_expat : emirateData?.minDP_national;

  const handleEmirateChange = (emirate) => {
    setSelectedEmirate(emirate);
    const data = emirateRates?.[emirate];
    setInterestRate(data?.baseRate?.toFixed(2));
    const newMinDP = buyerType === 'expat' ? data?.minDP_expat : data?.minDP_national;
    const currentDP = parseFloat(downPayment) || 0;
    if (currentDP < newMinDP) setDownPayment(String(newMinDP));
    setErrors({});
    triggerCalc();
  };

  const handleBuyerTypeChange = (type) => {
    setBuyerType(type);
    const newMinDP = type === 'expat' ? emirateData?.minDP_expat : emirateData?.minDP_national;
    const currentDP = parseFloat(downPayment) || 0;
    if (currentDP < newMinDP) setDownPayment(String(newMinDP));
    setErrors({});
    triggerCalc();
  };

  const validate = (field, value) => {
    const newErrors = { ...errors };
    const num = parseFloat(value?.replace?.(/,/g, '') || value);
    if (field === 'propertyPrice') {
      if (isNaN(num) || num <= 0) newErrors[field] = 'Must be greater than 0';
      else if (num > 500000000) newErrors[field] = 'Maximum AED 500M';
      else delete newErrors?.[field];
    }
    if (field === 'downPayment') {
      if (isNaN(num) || num < minDP) newErrors[field] = `Min ${minDP}% for ${buyerType === 'expat' ? 'expats' : 'UAE nationals'} in ${selectedEmirate}`;
      else if (num >= 100) newErrors[field] = 'Cannot be 100% or more';
      else delete newErrors?.[field];
    }
    if (field === 'interestRate') {
      if (isNaN(num) || num <= 0) newErrors[field] = 'Must be greater than 0';
      else if (num > 20) newErrors[field] = 'Maximum 20%';
      else delete newErrors?.[field];
    }
    if (field === 'tenure') {
      if (isNaN(num) || num < 1) newErrors[field] = 'Minimum 1 year';
      else if (num > 25) newErrors[field] = 'Maximum 25 years in UAE';
      else delete newErrors?.[field];
    }
    setErrors(newErrors);
  };

  const price = useMemo(() => Math.max(0, parseFloat(propertyPrice?.replace(/,/g, '')) || 0), [propertyPrice]);
  const dpPercent = useMemo(() => Math.max(0, parseFloat(downPayment) || 0), [downPayment]);
  const rate = useMemo(() => Math.max(0, parseFloat(interestRate) || 0), [interestRate]);
  const years = useMemo(() => Math.min(25, Math.max(1, parseInt(tenure) || 0)), [tenure]);

  const downPaymentAmount = price * (dpPercent / 100);
  const loanAmount = Math.max(0, price - downPaymentAmount);
  const monthlyRate = rate / 100 / 12;
  const numPayments = years * 12;
  const monthlyPayment = monthlyRate > 0 && numPayments > 0 && loanAmount > 0
    ? loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1)
    : 0;
  const totalPayment = monthlyPayment * numPayments;
  const totalInterest = Math.max(0, totalPayment - loanAmount);
  const dldFee = price * (emirateData?.transferFee / 100);
  const adminFee = emirateData?.adminFee;
  const agentFee = price * 0.02;
  const totalCost = price + dldFee + adminFee + agentFee;
  const hasErrors = Object.keys(errors)?.length > 0;

  const banks = [
    { name: 'Emirates NBD', rate: `${emirateData?.baseRate?.toFixed(2)}%`, maxLTV: buyerType === 'expat' ? `${100 - minDP}%` : `${100 - (buyerType === 'expat' ? emirateData?.minDP_expat : emirateData?.minDP_national)}%`, minIncome: 'AED 15K', type: 'Fixed 1yr then variable', badge: 'Popular' },
    { name: 'HSBC UAE', rate: `${(emirateData?.baseRate - 0.24)?.toFixed(2)}%`, maxLTV: buyerType === 'expat' ? `${100 - minDP}%` : '80%', minIncome: 'AED 20K', type: 'Variable rate', badge: 'Best Rate' },
    { name: 'Mashreq Bank', rate: `${(emirateData?.baseRate + 0.16)?.toFixed(2)}%`, maxLTV: buyerType === 'expat' ? `${100 - minDP - 5}%` : '80%', minIncome: 'AED 12K', type: 'Fixed 3yr then variable', badge: '' },
    { name: 'ADCB', rate: `${(emirateData?.baseRate - 0.14)?.toFixed(2)}%`, maxLTV: buyerType === 'expat' ? `${100 - minDP}%` : '80%', minIncome: 'AED 15K', type: 'Variable rate', badge: 'Fast Approval' },
    { name: 'Dubai Islamic Bank', rate: `${(emirateData?.baseRate + 0.06)?.toFixed(2)}%`, maxLTV: buyerType === 'expat' ? `${100 - minDP}%` : '80%', minIncome: 'AED 10K', type: 'Islamic (Murabaha)', badge: 'Islamic' },
    { name: 'Standard Chartered', rate: `${(emirateData?.baseRate + 0.06)?.toFixed(2)}%`, maxLTV: buyerType === 'expat' ? `${100 - minDP - 5}%` : '80%', minIncome: 'AED 18K', type: 'Variable rate', badge: '' },
  ];

  const fmt = (n) => Math.round(Math.max(0, n))?.toLocaleString('en-AE');

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #020B18 0%, #041428 50%, #020B18 100%)' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 px-4 py-3" style={{ background: 'rgba(2, 11, 24, 0.95)', borderBottom: '1px solid rgba(0, 212, 255, 0.15)', backdropFilter: 'blur(12px)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 transition-opacity hover:opacity-80">
            <Icon name="ArrowLeft" size={18} color="#00D4FF" />
            <span className="text-sm" style={{ color: '#00D4FF' }}>Back to Home</span>
          </button>
          <div className="flex items-center gap-2">
            <Icon name="Calculator" size={18} color="#00D4FF" />
            <span className="font-bold text-sm md:text-base" style={{ color: '#E8F4FF', fontFamily: "'Orbitron', sans-serif" }}>UAE Mortgage & Finance</span>
          </div>
          <button onClick={() => navigate('/expat-buyer-guidance')} className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200" style={{ background: 'rgba(0, 212, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.3)', color: '#00D4FF' }}>
            Expat Guide
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(0, 212, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.3)', color: '#00D4FF' }}>
            <span>🇦🇪</span>
            <span>UAE MORTGAGE & FINANCE TOOLS</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            <span style={{ color: '#00D4FF' }}>UAE Mortgage</span>
            <span style={{ color: '#E8F4FF' }}> & Finance Tools</span>
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: '#6B9EC4' }}>UAE-specific mortgage calculators with emirate-specific bank rates, LTV ratios, transfer fees, and total cost-of-ownership breakdowns in AED.</p>
        </div>

        {/* Emirate Selector */}
        <div className="mb-6">
          <div className="flex flex-wrap justify-center gap-2">
            {Object.keys(emirateRates)?.map(emirate => (
              <button
                key={emirate}
                onClick={() => handleEmirateChange(emirate)}
                className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200"
                style={selectedEmirate === emirate
                  ? { background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' }
                  : { background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(0, 212, 255, 0.2)', color: '#6B9EC4' }
                }
              >
                {emirate}
              </button>
            ))}
          </div>
          <div className="flex justify-center gap-6 mt-3">
            <span className="text-xs" style={{ color: '#4A7A9B' }}>Base Rate: <span style={{ color: '#00D4FF' }}>{emirateData?.baseRate?.toFixed(2)}%</span></span>
            <span className="text-xs" style={{ color: '#4A7A9B' }}>Transfer Fee: <span style={{ color: '#00D4FF' }}>{emirateData?.transferFee}%</span></span>
            <span className="text-xs" style={{ color: '#4A7A9B' }}>Min Expat DP: <span style={{ color: '#00D4FF' }}>{emirateData?.minDP_expat}%</span></span>
          </div>
        </div>

        {/* Buyer Type Toggle */}
        <div className="flex justify-center gap-3 mb-8">
          {['expat', 'uae_national']?.map(type => (
            <button key={type} onClick={() => handleBuyerTypeChange(type)}
              className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
              style={buyerType === type ? { background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' } : { background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(0, 212, 255, 0.2)', color: '#6B9EC4' }}
            >
              {type === 'expat' ? '🌍 Expat Buyer' : '🇦🇪 UAE National'}
            </button>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[{ id: 'calculator', label: 'Mortgage Calculator', icon: 'Calculator' }, { id: 'banks', label: 'Bank Comparison', icon: 'Landmark' }, { id: 'costs', label: 'Total Cost Breakdown', icon: 'DollarSign' }, { id: 'eligibility', label: 'Eligibility Check', icon: 'CheckCircle' }]?.map(tab => (
            <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
              style={activeTab === tab?.id ? { background: 'rgba(0, 212, 255, 0.2)', border: '1px solid rgba(0, 212, 255, 0.6)', color: '#00D4FF' } : { background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(0, 212, 255, 0.15)', color: '#6B9EC4' }}
            >
              <Icon name={tab?.icon} size={14} color={activeTab === tab?.id ? '#00D4FF' : '#6B9EC4'} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 md:p-8 rounded-2xl" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
              <h2 className="text-lg font-bold mb-6" style={{ color: '#E8F4FF' }}>Mortgage Calculator — {selectedEmirate}</h2>
              {hasErrors && (
                <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(255,100,100,0.08)', border: '1px solid rgba(255,100,100,0.3)' }}>
                  <div className="flex items-center gap-2">
                    <Icon name="AlertCircle" size={14} color="#FF6464" />
                    <span className="text-xs" style={{ color: '#FF6464' }}>Please fix the errors below for accurate results</span>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#6B9EC4' }}>Property Price (AED)</label>
                  <input type="text" value={propertyPrice} onChange={e => { setPropertyPrice(e?.target?.value); validate('propertyPrice', e?.target?.value); }}
                    placeholder="1,500,000"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'rgba(0, 212, 255, 0.05)', border: `1px solid ${errors?.propertyPrice ? '#FF6464' : 'rgba(0, 212, 255, 0.2)'}`, color: '#E8F4FF' }}
                  />
                  {errors?.propertyPrice && <p className="text-xs mt-1" style={{ color: '#FF6464' }}>{errors?.propertyPrice}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#6B9EC4' }}>Down Payment (%) — Min {minDP}% for {buyerType === 'expat' ? 'Expats' : 'UAE Nationals'} in {selectedEmirate}</label>
                  <input type="text" value={downPayment} onChange={e => { setDownPayment(e?.target?.value); validate('downPayment', e?.target?.value); }}
                    placeholder={String(minDP)}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'rgba(0, 212, 255, 0.05)', border: `1px solid ${errors?.downPayment ? '#FF6464' : 'rgba(0, 212, 255, 0.2)'}`, color: '#E8F4FF' }}
                  />
                  {errors?.downPayment && <p className="text-xs mt-1" style={{ color: '#FF6464' }}>{errors?.downPayment}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#6B9EC4' }}>Annual Interest Rate (%) — {selectedEmirate} base: {emirateData?.baseRate?.toFixed(2)}%</label>
                  <input type="text" value={interestRate} onChange={e => { setInterestRate(e?.target?.value); validate('interestRate', e?.target?.value); }}
                    placeholder={emirateData?.baseRate?.toFixed(2)}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'rgba(0, 212, 255, 0.05)', border: `1px solid ${errors?.interestRate ? '#FF6464' : 'rgba(0, 212, 255, 0.2)'}`, color: '#E8F4FF' }}
                  />
                  {errors?.interestRate && <p className="text-xs mt-1" style={{ color: '#FF6464' }}>{errors?.interestRate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#6B9EC4' }}>Loan Tenure (Years) — Max 25 years</label>
                  <input type="text" value={tenure} onChange={e => { setTenure(e?.target?.value); validate('tenure', e?.target?.value); }}
                    placeholder="25"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{ background: 'rgba(0, 212, 255, 0.05)', border: `1px solid ${errors?.tenure ? '#FF6464' : 'rgba(0, 212, 255, 0.2)'}`, color: '#E8F4FF' }}
                  />
                  {errors?.tenure && <p className="text-xs mt-1" style={{ color: '#FF6464' }}>{errors?.tenure}</p>}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-6 rounded-2xl" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
                <h3 className="font-bold mb-4" style={{ color: '#E8F4FF' }}>Monthly Payment</h3>
                <div className="relative">
                  {isCalculating && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg" style={{ background: 'rgba(10,22,40,0.75)', backdropFilter: 'blur(2px)' }}>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: '#00D4FF', borderTopColor: 'transparent' }} />
                        <span className="text-xs font-medium" style={{ color: '#00D4FF' }}>Recalculating...</span>
                      </div>
                    </div>
                  )}
                  <div className="text-4xl font-bold mb-1" style={{ color: hasErrors ? '#6B9EC4' : '#00D4FF', fontFamily: "'JetBrains Mono', monospace" }}>AED {fmt(monthlyPayment)}</div>
                  <div className="text-sm" style={{ color: '#6B9EC4' }}>per month for {years} years</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Down Payment', value: `AED ${fmt(downPaymentAmount)}`, sub: `${dpPercent}% of price` },
                  { label: 'Loan Amount', value: `AED ${fmt(loanAmount)}`, sub: `${(100 - dpPercent)?.toFixed(0)}% LTV` },
                  { label: 'Total Interest', value: `AED ${fmt(totalInterest)}`, sub: 'Over loan tenure' },
                  { label: 'Total Repayment', value: `AED ${fmt(totalPayment)}`, sub: 'Principal + Interest' },
                ]?.map((item, idx) => (
                  <div key={idx} className="p-4 rounded-xl" style={{ background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(0, 212, 255, 0.15)' }}>
                    <div className="text-xs mb-1" style={{ color: '#6B9EC4' }}>{item?.label}</div>
                    <div className="font-bold text-sm" style={{ color: '#00D4FF' }}>{item?.value}</div>
                    <div className="text-xs" style={{ color: '#4A7A9B' }}>{item?.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bank Comparison Tab */}
        {activeTab === 'banks' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {banks?.map((bank, idx) => (
              <div key={idx} className="p-5 rounded-2xl" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold" style={{ color: '#E8F4FF' }}>{bank?.name}</h3>
                  {bank?.badge && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0, 212, 255, 0.15)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.3)' }}>{bank?.badge}</span>}
                </div>
                <div className="text-3xl font-bold mb-1" style={{ color: '#00D4FF', fontFamily: "'JetBrains Mono', monospace" }}>{bank?.rate}</div>
                <div className="text-xs mb-4" style={{ color: '#6B9EC4' }}>{bank?.type}</div>
                <div className="space-y-2">
                  {[{ label: 'Max LTV', value: bank?.maxLTV }, { label: 'Min Income', value: bank?.minIncome }]?.map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-xs" style={{ color: '#6B9EC4' }}>{item?.label}</span>
                      <span className="text-xs font-medium" style={{ color: '#E8F4FF' }}>{item?.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Total Cost Tab */}
        {activeTab === 'costs' && (
          <div className="max-w-2xl mx-auto p-6 md:p-8 rounded-2xl" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
            <h2 className="text-lg font-bold mb-2" style={{ color: '#E8F4FF' }}>Total Cost of Ownership — {selectedEmirate}</h2>
            <p className="text-xs mb-6" style={{ color: '#6B9EC4' }}>Transfer fee: {emirateData?.transferFee}% | Admin fee: AED {emirateData?.adminFee?.toLocaleString()}</p>
            <div className="space-y-3">
              {[
                { label: 'Property Price', value: fmt(price) },
                { label: `Transfer Fee (${emirateData?.transferFee}%)`, value: fmt(dldFee), note: `Paid to ${selectedEmirate} land authority` },
                { label: 'Admin Fee', value: fmt(adminFee), note: 'Fixed registration fee' },
                { label: 'Agent Commission (2%)', value: fmt(agentFee), note: 'Negotiable' },
                { label: 'TOTAL ACQUISITION COST', value: fmt(totalCost), highlight: true },
              ]?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 rounded-xl"
                  style={item?.highlight ? { background: 'rgba(0, 212, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.4)' } : { background: 'rgba(0, 212, 255, 0.03)', border: '1px solid rgba(0, 212, 255, 0.1)' }}
                >
                  <div>
                    <div className="text-sm font-medium" style={{ color: item?.highlight ? '#00D4FF' : '#E8F4FF' }}>{item?.label}</div>
                    {item?.note && <div className="text-xs" style={{ color: '#4A7A9B' }}>{item?.note}</div>}
                  </div>
                  <div className="font-bold" style={{ color: item?.highlight ? '#00D4FF' : '#E8F4FF', fontFamily: "'JetBrains Mono', monospace" }}>AED {item?.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Eligibility Tab */}
        {activeTab === 'eligibility' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Expat Requirements', icon: '🌍', items: [`Valid UAE residence visa`, 'Minimum 6 months employment', 'Min income AED 10,000–20,000/mo (bank-dependent)', 'Max 50% debt burden ratio', 'Clean credit history', `${emirateData?.minDP_expat}–${emirateData?.minDP_expat + 5}% down payment ready (${selectedEmirate})`, 'Property in freehold zone'] },
              { title: 'UAE National Requirements', icon: '🇦🇪', items: ['Emirates ID', 'Minimum 3 months employment', 'Min income AED 8,000/mo', 'Max 50% debt burden ratio', 'Clean Al Etihad Credit Bureau report', `${emirateData?.minDP_national}% down payment (${selectedEmirate})`, 'Any property type eligible'] },
            ]?.map((section, idx) => (
              <div key={idx} className="p-6 rounded-2xl" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E8F4FF' }}>{section?.icon} {section?.title}</h3>
                <div className="space-y-2">
                  {section?.items?.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Icon name="CheckCircle" size={14} color="#00D4FF" />
                      <span className="text-sm" style={{ color: '#6B9EC4' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10 p-8 rounded-2xl" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
          <h2 className="text-xl font-bold mb-3" style={{ color: '#E8F4FF' }}>Ready to Apply for a UAE Mortgage?</h2>
          <p className="text-sm mb-6" style={{ color: '#6B9EC4' }}>Connect with RERA-registered mortgage brokers and get pre-approved in 48 hours</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button onClick={() => navigate('/professional-marketplace')} className="px-8 py-3 rounded-xl font-semibold text-sm transition-all duration-200" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000', boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)' }}>Find Mortgage Brokers →</button>
            <button onClick={() => navigate('/expat-buyer-guidance')} className="px-8 py-3 rounded-xl font-semibold text-sm transition-all duration-200" style={{ background: 'transparent', border: '2px solid rgba(0, 212, 255, 0.5)', color: '#00D4FF' }}>Expat Buyer Guide →</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UAEMortgageFinance;